import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Typography } from "@mui/material";
import {
  DataGridTable,
  GridPillChip,
  listTableStickyBodyScrollSx,
  type DataGridTableRow,
} from "design-components";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { OperatorDetailCustomerRow } from "../types";

type OperatorCustomersGridRow = OperatorDetailCustomerRow & DataGridTableRow;

const HEADER_CLASSES = [
  "operator-detail-customers-table__th-company-name",
  "operator-detail-customers-table__th-location",
  "operator-detail-customers-table__th-primary-contact",
  "operator-detail-customers-table__th-email",
  "operator-detail-customers-table__th-status",
] as const;

const noopSelection = () => {};

export interface OperatorDetailCustomersTableProps {
  rows: OperatorDetailCustomerRow[];
  sortDescending: boolean;
  onCompanyNameHeaderClick: () => void;
  paginationFooter?: ReactNode;
}

export function OperatorDetailCustomersTable(props: OperatorDetailCustomersTableProps) {
  const { rows, sortDescending, onCompanyNameHeaderClick, paginationFooter } = props;

  const gridRows: OperatorCustomersGridRow[] = rows.map((row) => ({
    ...row,
    primaryCellLabel: row.companyName,
  }));

  return (
    <DataGridTable<OperatorCustomersGridRow>
      className="users-table operator-detail-customers-table operator-detail-customers-table--aviro"
      sx={{ width: "100%", maxWidth: "100%" }}
      tableContainerClassName="users-table__container"
      tableClassName="users-table__table"
      headRowClassName="users-table__head-row"
      selectionHeaderCellClassName="users-table__th-checkbox"
      selectionBodyCellClassName="users-table__cell-checkbox"
      headerDataCellClassName={(columnIndex) => HEADER_CLASSES[columnIndex]}
      rows={gridRows}
      dataColumnCount={5}
      selectionMode="multiple"
      selectedRowIds={[]}
      onSelectedRowIdsChange={noopSelection}
      trailingEmptyCell={false}
      getRowStateClass={() => "normal"}
      rowHover
      showSelectionColumn={false}
      footer={paginationFooter}
      stickyHeader={Boolean(paginationFooter)}
      tableContainerSx={paginationFooter ? listTableStickyBodyScrollSx : undefined}
      renderHeaderCell={(columnIndex) => {
        if (columnIndex === 0) {
          return (
            <button type="button" className="users-table__sort-btn" onClick={onCompanyNameHeaderClick}>
              <Typography className="aviro-grid-header__cell-text" component="span">
                Company name
              </Typography>
              {sortDescending ? (
                <KeyboardArrowDownIcon className="users-table__sort-icon" aria-hidden />
              ) : (
                <KeyboardArrowUpIcon className="users-table__sort-icon" aria-hidden />
              )}
            </button>
          );
        }
        if (columnIndex === 1) {
          return <Typography className="aviro-grid-header__cell-text">Location</Typography>;
        }
        if (columnIndex === 2) {
          return <Typography className="aviro-grid-header__cell-text">Primary contact</Typography>;
        }
        if (columnIndex === 3) {
          return <Typography className="aviro-grid-header__cell-text">Email</Typography>;
        }
        return <Typography className="aviro-grid-header__cell-text">Status</Typography>;
      }}
      renderBodyCell={(row, columnIndex) => {
        if (columnIndex === 0) {
          return (
            <Link
              to={`/customers/${encodeURIComponent(row.customerId)}`}
              className="users-table__customer-name-link"
              onClick={(e) => e.stopPropagation()}
            >
              {row.companyName}
            </Link>
          );
        }
        if (columnIndex === 1) {
          return <Typography className="aviro-grid-table-cell-text">{row.locationLabel}</Typography>;
        }
        if (columnIndex === 2) {
          return <Typography className="aviro-grid-table-cell-text">{row.primaryContact}</Typography>;
        }
        if (columnIndex === 3) {
          return (
            <Typography className="aviro-grid-table-cell-text users-table__cell-ellipsis">{row.email}</Typography>
          );
        }
        return (
          <GridPillChip
            className="users-table__status-pill users-table__status-pill--customer-active operators-table__status-pill"
            pillVariant={row.status === "active" ? "breeze" : "critical"}
            label={row.status === "active" ? "Active" : "Inactive"}
            size="small"
          />
        );
      }}
    />
  );
}
